import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Settings, Image, Upload, CheckCircle, X, Link, FileImage, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImageUploader = ({ 
  title, 
  description,
  imageType, 
  currentImage, 
  onImageUpdate,
  testIdPrefix
}) => {
  const [uploading, setUploading] = useState(false);
  const [urlMode, setUrlMode] = useState(true);
  const [urlInput, setUrlInput] = useState('');
  const [previewImage, setPreviewImage] = useState(currentImage || '');
  const fileInputRef = useRef(null);

  useEffect(() => {
    setPreviewImage(currentImage || '');
    if (currentImage && !currentImage.startsWith('/api/uploads/')) {
      setUrlInput(currentImage);
    }
  }, [currentImage]);

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/api/uploads/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return url;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de archivo no permitido. Use JPG, PNG, GIF o WebP');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo excede el tamaño máximo de 5MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/upload/${imageType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setPreviewImage(res.data.url);
      onImageUpdate(res.data.url);
      toast.success('Imagen subida exitosamente');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al subir imagen';
      toast.error(message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUrlSave = async () => {
    if (!urlInput.trim()) {
      toast.error('Ingrese una URL válida');
      return;
    }

    setUploading(true);
    try {
      await axios.put(`${API}/configuracion`, {
        [`${imageType}_image`]: urlInput
      });
      
      setPreviewImage(urlInput);
      onImageUpdate(urlInput);
      toast.success('URL de imagen guardada');
    } catch (error) {
      toast.error('Error al guardar URL');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async () => {
    setUploading(true);
    try {
      await axios.put(`${API}/configuracion`, {
        [`${imageType}_image`]: ''
      });
      
      setPreviewImage('');
      setUrlInput('');
      onImageUpdate('');
      toast.success('Imagen eliminada');
    } catch (error) {
      toast.error('Error al eliminar imagen');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="border border-slate-200 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Image className="w-5 h-5 text-teal-500" />
          {title}
        </CardTitle>
        {description && (
          <p className="text-sm text-slate-500">{description}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Toggle Buttons */}
        <div className="flex gap-2">
          <Button
            type="button"
            variant={urlMode ? "default" : "outline"}
            size="sm"
            onClick={() => setUrlMode(true)}
            className={urlMode ? "bg-teal-500 hover:bg-teal-600" : ""}
            data-testid={`${testIdPrefix}-url-mode-btn`}
          >
            <Link className="w-4 h-4 mr-2" />
            URL Externa
          </Button>
          <Button
            type="button"
            variant={!urlMode ? "default" : "outline"}
            size="sm"
            onClick={() => setUrlMode(false)}
            className={!urlMode ? "bg-teal-500 hover:bg-teal-600" : ""}
            data-testid={`${testIdPrefix}-upload-mode-btn`}
          >
            <FileImage className="w-4 h-4 mr-2" />
            Subir Archivo
          </Button>
        </div>

        {/* URL Mode */}
        {urlMode && (
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>URL de la imagen</Label>
              <Input
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://ejemplo.com/imagen.jpg"
                data-testid={`${testIdPrefix}-url-input`}
              />
            </div>
            <Button
              onClick={handleUrlSave}
              disabled={uploading || !urlInput.trim()}
              className="w-full bg-teal-500 hover:bg-teal-600"
              data-testid={`${testIdPrefix}-save-url-btn`}
            >
              {uploading ? 'Guardando...' : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Guardar URL
                </>
              )}
            </Button>
          </div>
        )}

        {/* Upload Mode */}
        {!urlMode && (
          <div className="space-y-3">
            <div 
              className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-teal-400 hover:bg-teal-50/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              data-testid={`${testIdPrefix}-dropzone`}
            >
              <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <p className="text-sm text-slate-600 font-medium">
                Haga clic para seleccionar una imagen
              </p>
              <p className="text-xs text-slate-400 mt-1">
                JPG, PNG, GIF o WebP (máx. 5MB)
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleFileSelect}
                className="hidden"
                data-testid={`${testIdPrefix}-file-input`}
              />
            </div>
            {uploading && (
              <div className="flex items-center justify-center gap-2 text-sm text-teal-600">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-teal-500 border-t-transparent"></div>
                Subiendo imagen...
              </div>
            )}
          </div>
        )}

        {/* Image Preview - Auto height to show full image */}
        {previewImage && (
          <div className="relative">
            <div className="relative rounded-lg overflow-hidden border border-slate-200 bg-slate-50 p-2">
              <img
                src={getFullImageUrl(previewImage)}
                alt={`Preview ${title}`}
                className="w-full h-auto object-contain max-h-40"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/400x100?text=Error+de+imagen';
                }}
                data-testid={`${testIdPrefix}-preview`}
              />
            </div>
            <button
              onClick={handleRemoveImage}
              className="absolute top-4 right-4 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
              title="Eliminar imagen"
              data-testid={`${testIdPrefix}-remove-btn`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const Configuracion = () => {
  const { isAdmin } = useAuth();
  const [config, setConfig] = useState({
    logo_image: '',
    login_image: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/configuracion`);
      setConfig({
        logo_image: res.data.logo_image || '',
        login_image: res.data.login_image || ''
      });
    } catch (error) {
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (!isAdmin()) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Settings className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No tienes permisos para acceder a esta sección</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Configuración</h1>
        <p className="text-slate-500">Personalización del sistema</p>
      </div>

      {/* Images Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ImageUploader
          title="Logo del Sistema"
          description="Imagen principal del sistema. Aparece en el menú lateral y centrado en la página de login."
          imageType="logo"
          currentImage={config.logo_image}
          onImageUpdate={(url) => setConfig(prev => ({ ...prev, logo_image: url }))}
          testIdPrefix="logo-image"
        />

        <ImageUploader
          title="Imagen de Fondo del Login"
          description="Imagen de fondo en la página de inicio de sesión."
          imageType="login"
          currentImage={config.login_image}
          onImageUpdate={(url) => setConfig(prev => ({ ...prev, login_image: url }))}
          testIdPrefix="login-image"
        />
      </div>

      {/* Info Card */}
      <Card className="border border-slate-200 shadow-sm bg-teal-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <ImageIcon className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <p className="font-medium text-slate-900">Opciones de imagen</p>
              <p className="text-sm text-slate-600 mt-1">
                Puedes subir imágenes directamente (JPG, PNG, GIF, WebP hasta 5MB) o usar URLs externas.
                El logo se mostrará en el menú lateral y en la página de login.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
